import React, { useEffect, useState } from "react";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { url } from "../constants/url";
import { useNavigate } from "react-router-dom";

function ReviewRating() {
  const [expandedReviews, setExpandedReviews] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [ratingCounts, setRatingCounts] = useState({
    5: 0,
    4: 0,
    3: 0,
    2: 0,
    1: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const toggleReview = (index) => {
    setExpandedReviews((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    axios
      .get(`${url}/api/admin/feedbacks`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined
        }
      })
      .then((res) => {
        const fb = res.data.feedback || [];
        setReviews(
          fb.map((f) => ({
            id: f.id,
            author: f.user?.fullName || "Anonymous",
            rating: f.starsCount,
            date: new Date(f.createdAt).toLocaleDateString(),
            content: f.text
          }))
        );

        const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        fb.forEach((f) => {
          counts[f.starsCount] = (counts[f.starsCount] || 0) + 1;
        });
        setRatingCounts(counts);
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          localStorage.clear();
          navigate("/login");
        } else {
          console.error("Error fetching feedback:", err);
          setError("Failed to load feedback. Please try again later.");
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const totalRatings = Object.values(ratingCounts).reduce((a, b) => a + b, 0);
  const averageRating = totalRatings
    ? (5 * ratingCounts[5] +
        4 * ratingCounts[4] +
        3 * ratingCounts[3] +
        2 * ratingCounts[2] +
        1 * ratingCounts[1]) /
      totalRatings
    : 0;
  const getPercentage = (count) =>
    totalRatings ? (count / totalRatings) * 100 : 0;

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto bg-white p-6 rounded-lg shadow-sm">
        {/* … your skeleton layout exactly as before … */}
        <div className="flex flex-col md:flex-row gap-8 mb-8">
          <div className="flex flex-col items-center justify-center">
            <Skeleton height={40} width={80} />
            <Skeleton height={15} width={50} />
            <div className="flex items-center mt-1 space-x-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} circle height={20} width={20} />
              ))}
            </div>
            <Skeleton height={12} width={100} className="mt-1" />
          </div>
          <div className="flex-1 space-y-2">
            {[5, 4, 3, 2, 1].map((rating) => (
              <div key={rating} className="flex items-center">
                <Skeleton height={15} width={50} />
                <div className="flex-1 h-6 bg-gray-200 rounded-sm mx-2">
                  <Skeleton
                    height={6}
                    width={`${getPercentage(ratingCounts[rating])}%`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="border-t pt-4 space-y-4">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="border-b pb-4">
              <Skeleton height={20} width={150} />
              <Skeleton count={3} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto bg-white p-6 rounded-lg shadow-sm">
        <p className="text-red-500 text-center">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto bg-white p-6 rounded-lg shadow-sm">
      {/* Rating Summary */}
      <div className="flex flex-col md:flex-row gap-8 mb-8">
        <div className="flex flex-col items-center">
          <h2 className="text-3xl font-bold">{averageRating.toFixed(2)}</h2>
          <p className="text-gray-500 text-sm">Overall</p>
          <div className="flex items-center mt-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <StarIcon key={star} filled={star <= Math.round(averageRating)} />
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Based on {totalRatings} Rating{totalRatings !== 1 && "s"}
          </p>
        </div>
        <div className="flex-1">
          {[5, 4, 3, 2, 1].map((rating) => (
            <div key={rating} className="flex items-center mb-2">
              <span className="text-sm w-12">{rating} Star</span>
              <div className="flex-1 h-6 bg-gray-200 rounded-sm mx-2">
                <div
                  className="h-full bg-amber-300 rounded-sm"
                  style={{ width: `${getPercentage(ratingCounts[rating])}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reviews List */}
      <div className="border-t pt-4">
        {reviews.map((review, idx) => (
          <div key={review.id} className="border-b pb-4 mb-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center mb-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarIcon
                      key={star}
                      filled={star <= review.rating}
                      className="w-4 h-4"
                    />
                  ))}
                </div>
                <h3 className="font-medium">{review.author}</h3>
                <div className="flex items-center text-sm text-gray-500">
                  <USFlag />
                  <span className="ml-1">{review.country}</span>
                </div>
              </div>
              <span className="text-xs text-gray-500">{review.date}</span>
            </div>
            <p className="text-sm text-gray-700 mt-1">
              {expandedReviews.includes(idx)
                ? review.content
                : `${review.content.slice(0, 150)}…`}
            </p>
            <button
              onClick={() => toggleReview(idx)}
              className="text-sm font-medium mt-1 text-blue-600 hover:text-blue-800"
            >
              {expandedReviews.includes(idx) ? "Show Less" : "Show More"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function StarIcon({ filled = false, className = "w-5 h-5" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke={filled ? "none" : "currentColor"}
      className={`${className} ${filled ? "text-amber-400" : "text-gray-300"}`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
      />
    </svg>
  );
}

function USFlag() {
  return;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 16"
      className="w-4 h-3"
    >
      <rect width="24" height="16" fill="#f0f0f0" />
      {[...Array(7)].map((_, i) => (
        <rect key={i} y={i * 2.46} width="24" height="1.23" fill="#d80027" />
      ))}
      <rect width="12" height="8.62" fill="#2e3560" />
    </svg>
  );
}

export default function FeedbackListPage() {
  return (
    <main className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Product Reviews</h1>
        <ReviewRating />
      </div>
    </main>
  );
}
